import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Statistic, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import {
  fetchConfusionMatrix,
  fetchRecognitionAttempts,
  fetchRecognitionSummary,
  type RecognitionAnalyticsSummary,
} from '@/services/recognition.service';

const { Title, Text } = Typography;

export const RecognitionAnalyticsPage: React.FC = () => {
  const [summary, setSummary] = useState<RecognitionAnalyticsSummary | null>(null);
  const [attempts, setAttempts] = useState<Record<string, unknown>[]>([]);
  const [confusion, setConfusion] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const [s, a, c] = await Promise.all([
          fetchRecognitionSummary(),
          fetchRecognitionAttempts({ page: 1, pageSize: 20 }),
          fetchConfusionMatrix(15),
        ]);
        setSummary(s);
        setAttempts(a.data);
        setConfusion(c);
      } catch {
        message.error('Failed to load recognition analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadPage = async (p: number) => {
    setPage(p);
    try {
      const a = await fetchRecognitionAttempts({ page: p, pageSize: 20 });
      setAttempts(a.data);
    } catch {
      message.error('Failed to load attempts');
    }
  };

  const attemptColumns: ColumnsType<Record<string, unknown>> = [
    {
      title: 'Time',
      dataIndex: 'created_at',
      width: 160,
      render: (v) => (v ? String(v).replace('T', ' ').slice(0, 19) : '—'),
    },
    { title: 'Source', dataIndex: 'match_source', width: 100 },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      width: 90,
      render: (v) => (v != null ? `${(Number(v) * 100).toFixed(0)}%` : '—'),
    },
    { title: 'Selected', dataIndex: 'selected_item_code', width: 120, ellipsis: true },
    { title: 'Provider', dataIndex: 'provider_model', width: 120, ellipsis: true },
    { title: 'ms', dataIndex: 'latency_ms', width: 64 },
    { title: 'Failure', dataIndex: 'failure_reason', ellipsis: true },
  ];

  const confusionColumns: ColumnsType<Record<string, unknown>> = [
    { title: 'Predicted', dataIndex: 'predicted_code', width: 120 },
    { title: 'Predicted name', dataIndex: 'predicted_name', ellipsis: true },
    { title: 'Corrected', dataIndex: 'corrected_code', width: 120 },
    { title: 'Corrected name', dataIndex: 'corrected_name', ellipsis: true },
    { title: 'Count', dataIndex: 'confusion_count', width: 70 },
  ];

  return (
    <div>
      <SmartBreadcrumbs />
      <Title level={3}>Recognition Analytics</Title>
      <Text type="secondary">Success rate, low-confidence cases, and confused product pairs.</Text>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic title="Total attempts" value={summary?.totalAttempts ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic title="Success rate" value={summary?.successRate ?? 0} suffix="%" precision={1} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="Avg confidence"
              value={summary?.avgConfidence != null ? summary.avgConfidence * 100 : 0}
              suffix="%"
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic title="Low confidence" value={summary?.lowConfidenceCount ?? 0} />
          </Card>
        </Col>
      </Row>

      <Card title="Provider performance" style={{ marginTop: 16 }} loading={loading}>
        <Table
          size="small"
          rowKey={(r) => String(r.providerModel)}
          pagination={false}
          dataSource={summary?.providerPerformance || []}
          columns={[
            { title: 'Model', dataIndex: 'providerModel' },
            { title: 'Attempts', dataIndex: 'attempts' },
            { title: 'Avg latency (ms)', dataIndex: 'avgLatencyMs' },
            { title: 'Errors', dataIndex: 'errors' },
          ]}
        />
      </Card>

      <Card title="Most confused products" style={{ marginTop: 16 }} loading={loading}>
        <Table size="small" rowKey={(_, i) => String(i)} columns={confusionColumns} dataSource={confusion} pagination={false} />
      </Card>

      <Card title="Recent attempts (low confidence highlighted)" style={{ marginTop: 16 }}>
        <Table
          size="small"
          rowKey="id"
          columns={attemptColumns}
          dataSource={attempts}
          loading={loading}
          pagination={{ current: page, pageSize: 20, onChange: (p) => void loadPage(p) }}
          rowClassName={(row) =>
            row.confidence != null && Number(row.confidence) < (summary?.thresholds.confirm ?? 0.55)
              ? 'recognition-low-confidence-row'
              : ''
          }
        />
      </Card>
    </div>
  );
};
